import BDS from "./BDS";
import { IconButton } from 'components/common';
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FaPlus, FaCopy } from 'react-icons/fa';
const Areas = ({
  projectData,
  useHookForm,
  areaObject,
  newArea,
  editArea,
  cloneArea,
  deleteArea,
  openWallData,
  newOpenWall,
  editOpenWall,
  deleteOpenWall,
  roofExtensionData,
  newRoofExtension,
  editRoofExtension,
  deleteRoofExtension,
  canopyData,
  newCanopy,
  editCanopy,
  deleteCanopy,
  fasciaData,
  newFascia,
  editFascia,
  deleteFascia,
  frameOpeningData,
  newFrameOpening,
  editFrameOpening,
  deleteFrameOpening,
  mezzanineData,
  newMezzanine,
  editMezzanine,
  deleteMezzanine,
  craneInformationData,
  newCraneInformation,
  editCraneInformation,
  deleteCraneInformation,
}) => {

  const { areaData } = areaObject
  const data = {
    projectData,
    useHookForm
  }

  // Active area + keyed lazy mount (mountOnEnter semantics): an area's BDS
  // content mounts only when its tab is first opened, then stays mounted and
  // is hidden with tw-hidden. Keyed by area.id so add/clone/delete never
  // remounts other areas' editors.
  const [activeAreaId, setActiveAreaId] = useState(areaData?.[0]?.id ?? null)
  const mountedIdsRef = useRef(new Set(activeAreaId != null ? [activeAreaId] : []))
  if (activeAreaId != null) mountedIdsRef.current.add(activeAreaId)

  // If the active area was deleted, fall back to the first area
  useEffect(() => {
    if (areaData?.length && !areaData.some(area => area.id === activeAreaId)) {
      setActiveAreaId(areaData[0].id)
    }
  }, [areaData, activeAreaId])

  return (
    <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200 tw-rounded-lg tw-overflow-hidden">
      <div className="tw-flex tw-items-center tw-gap-1 tw-m-3 tw-text-sm">
        <IconButton
          size="sm"
          icon={<FaPlus size={12} />}
          onClick={newArea}
        >
          <span className="tw-hidden xl:tw-inline-block">New Area</span>
        </IconButton>

        <IconButton
          size="sm"
          icon={<FaCopy size={12} />}
          onClick={cloneArea}
        >
          <span className="tw-hidden xl:tw-inline-block">Clone Area</span>
        </IconButton>
      </div>

      <div className="tw-flex tw-flex-col md:tw-flex-row">
        <div className="md:tw-w-2/12 tw-border-0 md:tw-border-r tw-border-solid tw-border-slate-200">
          <nav className="tw-flex md:tw-flex-col tw-gap-1 tw-p-2 tw-overflow-x-auto">
            {areaData && areaData.map((area) => (
              <button
                key={area?.id}
                type="button"
                onClick={() => setActiveAreaId(area.id)}
                className={`tw-text-left tw-px-3 tw-py-2 tw-rounded tw-text-sm tw-font-medium tw-border-0 tw-cursor-pointer tw-transition-colors tw-whitespace-nowrap ${
                  area.id === activeAreaId
                    ? 'tw-bg-primary-600 tw-text-white'
                    : 'tw-bg-transparent tw-text-slate-600 hover:tw-bg-slate-100'
                }`}
              >
                Area {area?.id}
              </button>
            ))}
          </nav>
        </div>
        <div className="md:tw-w-10/12 tw-bg-slate-50 tw-p-4">
          {areaData && areaData.filter(area => mountedIdsRef.current.has(area.id)).map((area) => (
            <div key={area.id} className={area.id === activeAreaId ? '' : 'tw-hidden'}>
              <BDS
                data={data}
                area={area}
                editArea={() => editArea(area)}
                deleteArea={() => deleteArea(area)}
                openWallData={openWallData}
                newOpenWall={newOpenWall}
                editOpenWall={editOpenWall}
                deleteOpenWall={deleteOpenWall}
                roofExtensionData={roofExtensionData}
                newRoofExtension={newRoofExtension}
                editRoofExtension={editRoofExtension}
                deleteRoofExtension={deleteRoofExtension}
                canopyData={canopyData}
                newCanopy={newCanopy}
                editCanopy={editCanopy}
                deleteCanopy={deleteCanopy}
                fasciaData={fasciaData}
                newFascia={newFascia}
                editFascia={editFascia}
                deleteFascia={deleteFascia}
                frameOpeningData={frameOpeningData}
                newFrameOpening={newFrameOpening}
                editFrameOpening={editFrameOpening}
                deleteFrameOpening={deleteFrameOpening}
                mezzanineData={mezzanineData}
                newMezzanine={newMezzanine}
                editMezzanine={editMezzanine}
                deleteMezzanine={deleteMezzanine}
                craneInformationData={craneInformationData}
                newCraneInformation={newCraneInformation}
                editCraneInformation={editCraneInformation}
                deleteCraneInformation={deleteCraneInformation}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
};

Areas.propTypes = {
  projectData: PropTypes.object.isRequired,
  useHookForm: PropTypes.object.isRequired,
  areaObject: PropTypes.object.isRequired,
  newArea: PropTypes.func.isRequired,
  editArea: PropTypes.func.isRequired,
  cloneArea: PropTypes.func.isRequired,
  deleteArea: PropTypes.func.isRequired,
  openWallData: PropTypes.func.isRequired,
  newOpenWall: PropTypes.func.isRequired,
  editOpenWall: PropTypes.func.isRequired,
  deleteOpenWall: PropTypes.func.isRequired,
  roofExtensionData: PropTypes.func.isRequired,
  newRoofExtension: PropTypes.func.isRequired,
  editRoofExtension: PropTypes.func.isRequired,
  deleteRoofExtension: PropTypes.func.isRequired,
  canopyData: PropTypes.func.isRequired,
  newCanopy: PropTypes.func.isRequired,
  editCanopy: PropTypes.func.isRequired,
  deleteCanopy: PropTypes.func.isRequired,
  fasciaData: PropTypes.func.isRequired,
  newFascia: PropTypes.func.isRequired,
  editFascia: PropTypes.func.isRequired,
  deleteFascia: PropTypes.func.isRequired,
  frameOpeningData: PropTypes.func.isRequired,
  newFrameOpening: PropTypes.func.isRequired,
  editFrameOpening: PropTypes.func.isRequired,
  deleteFrameOpening: PropTypes.func.isRequired,
  mezzanineData: PropTypes.func.isRequired,
  newMezzanine: PropTypes.func.isRequired,
  editMezzanine: PropTypes.func.isRequired,
  deleteMezzanine: PropTypes.func.isRequired,
  craneInformationData: PropTypes.func.isRequired,
  newCraneInformation: PropTypes.func.isRequired,
  editCraneInformation: PropTypes.func.isRequired,
  deleteCraneInformation: PropTypes.func.isRequired
}

export default Areas;
